import axios, {
  AxiosError,
  CanceledError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios';
import { useAuthStore } from '@/stores/useAuthStore';
import { useGuestStore } from '@/stores/useGuestStore';
import { usePreferenceStore } from '@/stores/usePreferenceStore';
import { AUTH_REFRESH_PATH, GLOW_API_URL, HEADER } from './config';
import type { Row, LocalSession } from './types';
import {
  currentLocalSession,
  isCurrentSession,
  requireCurrentSession,
  requireCurrentCredentialFlow,
} from './session';

interface RetriableConfig extends InternalAxiosRequestConfig {
  _retried?: boolean;
}

/** 모든 요청에 언어·통화·게스트 헤더를 붙인다. includeAuth 면 회원 토큰(없으면 게스트 토큰)을 Bearer 로 보낸다. */
export function applyGlowHeaders(
  client: AxiosInstance,
  {
    includeAuth,
    includeGuestHeader = true,
  }: { includeAuth: boolean; includeGuestHeader?: boolean },
) {
  client.interceptors.request.use((config) => {
    if (
      typeof navigator !== 'undefined' &&
      !navigator.onLine &&
      !['get', 'head', 'options'].includes(
        config.method?.toLowerCase() ?? 'get',
      )
    ) {
      throw new AxiosError('OFFLINE_SUBMISSION', 'ERR_NETWORK', config);
    }
    if (config.credentialFlow)
      requireCurrentCredentialFlow(config.credentialFlow);
    const { locale, currency } = usePreferenceStore.getState();
    const { guestToken } = useGuestStore.getState();
    if (config.localSession) requireCurrentSession(config.localSession);
    const { accessToken } = useAuthStore.getState();
    if (includeAuth)
      config.dispatchSession =
        config.localSession ?? config.dispatchSession ?? currentLocalSession();

    if (!includeAuth && !includeGuestHeader) {
      config.headers.delete('Authorization');
      config.headers.delete(HEADER.GUEST);
    }
    const anonymousOnly = !includeAuth && !includeGuestHeader;
    config.headers.set(
      HEADER.LOCALE,
      (anonymousOnly && config.headers.get(HEADER.LOCALE)) || locale || 'en',
    );
    config.headers.set(
      HEADER.CURRENCY,
      (anonymousOnly && config.headers.get(HEADER.CURRENCY)) || currency,
    );
    if (guestToken && includeGuestHeader) {
      config.headers.set(HEADER.GUEST, guestToken);
    }
    if (includeAuth) {
      const bearer = accessToken ?? guestToken;
      if (bearer) {
        if (config.dispatchSession && !isCurrentSession(config.dispatchSession))
          throw new CanceledError('SESSION_CHANGED');
        config.headers.set('Authorization', `Bearer ${bearer}`);
      }
    }
    return config;
  });
}

let refreshing:
  | { binding: LocalSession; token: string; promise: Promise<string | null> }
  | undefined;

/** Single flight per current session. Old refresh completions cannot replace or clear a new login. */
export function refreshAccessToken(): Promise<string | null> {
  if (typeof navigator !== 'undefined' && !navigator.onLine)
    return Promise.reject(new AxiosError('OFFLINE_REFRESH', 'ERR_NETWORK'));
  const binding = currentLocalSession();
  const token = useAuthStore.getState().refreshToken;
  if (!binding || !token) return Promise.resolve(null);
  if (
    refreshing &&
    refreshing.token === token &&
    isCurrentSession(refreshing.binding)
  )
    return refreshing.promise;
  const stillCurrent = () =>
    isCurrentSession(binding) && useAuthStore.getState().refreshToken === token;
  const promise = axios
    .post<Row<{ accessToken: string; refreshToken: string }>>(
      `${GLOW_API_URL}${AUTH_REFRESH_PATH}`,
      { refreshToken: token },
      {
        timeout: 15000,
        headers: {
          [HEADER.LOCALE]: usePreferenceStore.getState().locale ?? 'en',
        },
      },
    )
    .then(({ data }) => {
      if (!stillCurrent()) throw new CanceledError('SESSION_CHANGED');
      const tokens = data?.row;
      if (
        typeof tokens?.accessToken !== 'string' ||
        !tokens.accessToken ||
        typeof tokens.refreshToken !== 'string' ||
        !tokens.refreshToken
      )
        throw new Error('INVALID_REFRESH_RESPONSE');
      useAuthStore.getState().saveTokens(tokens);
      return tokens.accessToken;
    })
    .catch((error: unknown) => {
      if (!stillCurrent()) throw new CanceledError('SESSION_CHANGED');
      const status = axios.isAxiosError(error)
        ? error.response?.status
        : undefined;
      if (status === 400 || status === 401 || status === 403) {
        useAuthStore.getState().clear();
        return null;
      }
      throw error;
    })
    .finally(() => {
      if (refreshing?.promise === promise) refreshing = undefined;
    });
  refreshing = { binding, token, promise };
  return promise;
}

/** 401 응답이면 토큰을 갱신하고 한 번만 재시도한다. */
export function applyAuthRefresh(client: AxiosInstance) {
  client.interceptors.response.use(undefined, async (error: unknown) => {
    if (
      !axios.isAxiosError(error) ||
      error.response?.status !== 401 ||
      !error.config
    ) {
      throw error;
    }
    const config = error.config as RetriableConfig;
    if (
      config.skipAuthRetry ||
      config.url?.startsWith('/app/v1/auth/') ||
      config._retried ||
      !useAuthStore.getState().refreshToken
    ) {
      throw error;
    }
    if (!isCurrentSession(config.dispatchSession))
      throw new CanceledError('SESSION_CHANGED');
    config._retried = true;
    const accessToken = await refreshAccessToken();
    if (!accessToken) {
      throw error;
    }
    if (!isCurrentSession(config.dispatchSession))
      throw new CanceledError('SESSION_CHANGED');
    config.headers.set('Authorization', `Bearer ${accessToken}`);
    return client.request(config);
  });
}
